import { useState, useEffect, useCallback, useRef } from 'react';
import { renderMarkdown } from '@markdawn/core';

interface PresentationViewProps {
  markdown: string;
  onExit: () => void;
  theme: 'github-light' | 'github-dark';
}

interface Slide {
  html: string;
  rawMarkdown: string;
  hasMermaid: boolean;
}

function parseSlides(markdown: string): string[] {
  // Split markdown by slide delimiters: ---, # headings, ## headings
  const lines = markdown.split('\n');
  const slides: string[] = [];
  let currentSlide: string[] = [];

  // Simple approach: split by --- or # headings
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Check for slide delimiter (---)
    if (trimmed === '---') {
      // Save previous slide if it has content
      if (currentSlide.length > 0) {
        slides.push(currentSlide.join('\n'));
        currentSlide = [];
      }
    } else if (trimmed.startsWith('# ')) {
      // H1 starts a new slide
      if (currentSlide.length > 0) {
        slides.push(currentSlide.join('\n'));
        currentSlide = [];
      }
      currentSlide.push(line);
    } else {
      currentSlide.push(line);
    }
  }

  // Add the last slide
  if (currentSlide.length > 0) {
    slides.push(currentSlide.join('\n'));
  }

  // If no slides were created (no delimiters), treat entire content as one slide
  return slides.length > 0 ? slides : [markdown];
}

export default function PresentationView({
  markdown,
  onExit,
  theme,
}: PresentationViewProps) {
  const [slideMarkdowns] = useState<string[]>(() => parseSlides(markdown));
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const mermaidInitialized = useRef(false);

  // Render all slides on mount
  useEffect(() => {
    const renderAllSlides = async () => {
      const renderedSlides: Slide[] = [];
      let anyHasMermaid = false;

      for (const slideMarkdown of slideMarkdowns) {
        try {
          const result = await renderMarkdown(slideMarkdown, {
            theme,
            enableMermaid: true,
            enableMath: true,
            sanitize: true,
          });
          renderedSlides.push({
            rawMarkdown: slideMarkdown,
            html: result.html,
            hasMermaid: result.hasMermaid || false,
          });
          if (result.hasMermaid) {
            anyHasMermaid = true;
          }
        } catch (error) {
          console.error('Error rendering slide:', error);
          renderedSlides.push({
            rawMarkdown: slideMarkdown,
            html: '<p>Error rendering slide</p>',
            hasMermaid: false,
          });
        }
      }
      setSlides(renderedSlides);

      // Initialize Mermaid if any slide has diagrams
      if (anyHasMermaid) {
        const initMermaid = async () => {
          const mermaid = (await import('mermaid')).default;

          if (!mermaidInitialized.current) {
            mermaid.initialize({
              startOnLoad: false,
              theme: theme === 'github-dark' ? 'dark' : 'default',
            });
            mermaidInitialized.current = true;
          }

          // Run mermaid on next tick to ensure DOM is updated
          setTimeout(() => {
            mermaid.run({
              querySelector: '.mermaid',
            });
          }, 100);
        };

        initMermaid();
      }
    };

    renderAllSlides();
  }, [slideMarkdowns, theme]);

  // Re-run mermaid when slide changes
  useEffect(() => {
    if (slides.length > 0 && slides[currentSlide]?.hasMermaid) {
      const runMermaid = async () => {
        const mermaid = (await import('mermaid')).default;

        // Run mermaid on next tick to ensure DOM is updated
        setTimeout(() => {
          mermaid.run({
            querySelector: '.mermaid',
          });
        }, 100);
      };

      runMermaid();
    }
  }, [currentSlide, slides]);

  const goToNextSlide = useCallback(() => {
    setCurrentSlide((prev) => Math.min(prev + 1, slides.length - 1));
  }, [slides.length]);

  const goToPrevSlide = useCallback(() => {
    setCurrentSlide((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
        case 'PageDown':
          e.preventDefault();
          goToNextSlide();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault();
          goToPrevSlide();
          break;
        case 'Home':
          e.preventDefault();
          setCurrentSlide(0);
          break;
        case 'End':
          e.preventDefault();
          setCurrentSlide(slides.length - 1);
          break;
        case 'Escape':
          e.preventDefault();
          onExit();
          break;
      }
    },
    [goToNextSlide, goToPrevSlide, slides.length, onExit]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Auto-hide controls after 3 seconds of inactivity
  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const resetTimeout = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => setShowControls(false), 3000);
    };

    const handleMouseMove = () => resetTimeout();

    resetTimeout();
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const progress = slides.length > 1 ? ((currentSlide + 1) / slides.length) * 100 : 100;

  // Show loading while slides are being rendered
  if (slides.length === 0) {
    return (
      <div className={`fixed inset-0 z-50 flex items-center justify-center ${theme === 'github-dark' ? 'dark bg-gray-900' : 'bg-white'}`}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600 dark:text-gray-400">Preparing presentation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${theme === 'github-dark' ? 'dark bg-gray-900' : 'bg-white'}`}>
      {/* Main slide content - like preview mode */}
      <div className="flex-1 overflow-auto custom-scrollbar bg-gray-50 dark:bg-gray-900">
        <div
          className="preview-container"
          dangerouslySetInnerHTML={{ __html: slides[currentSlide]?.html || '' }}
        />
      </div>

      {/* Controls overlay */}
      <div
        className={`fixed inset-x-0 bottom-0 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Progress bar */}
        <div className="h-1 bg-gray-300 dark:bg-gray-700">
          <div
            className="h-full bg-blue-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Control buttons */}
        <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={goToPrevSlide}
              disabled={currentSlide === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Previous
            </button>
            <button
              onClick={goToNextSlide}
              disabled={currentSlide === slides.length - 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {currentSlide + 1} / {slides.length}
            </span>
            <button
              onClick={onExit}
              className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
            >
              Exit (Esc)
            </button>
          </div>
        </div>
      </div>

      {/* Keyboard shortcuts hint */}
      {showControls && (
        <div className="fixed top-4 right-4 bg-black/70 text-white text-xs px-3 py-2 rounded-md">
          <div className="font-semibold mb-1">Keyboard Shortcuts:</div>
          <div>← → Space: Navigate</div>
          <div>Home/End: First/Last slide</div>
          <div>Esc: Exit presentation</div>
        </div>
      )}
    </div>
  );
}
